import { useEffect, useRef, useState } from 'react';
import {
	GetPriceBreakUpResponse,
	GlobalAPIError,
	PaymentAuthorizeRequest,
	PaymentErrors,
	TelrPaymentAuthorizeRequest,
	paymentAuthorize,
	paymentCapture,
	telrAuthorizePayment,
} from '@shory-health-individual/customer-api';
import { PaymentProvider, PaymentRadioOptions, PaymentVendorProvider } from '../../../common';
import { useAppPlatformContextOptions } from '../../../contexts/app';
import {
	selectEncryptedJourneyId,
	selectPaymentError,
	selectSetJourneyPaymentError,
	useQuoteStore,
} from '../../../stores';
import { usePaymentPublicKey } from '../../app-init-data';
import useCurrencyAndVat from './useCurrencyAndVat';

export type ApplePaymentData = {
	data: string;
	header: {
		ephemeralPublicKey: string;
		publicKeyHash: string;
		transactionId: string;
	};
	signature: string;
	version: string;
};

type ApplePaymentMethod = {
	displayName: string;
	network: string;
	type: string;
};

export type ApplePaymentDataInfo = {
	paymentData: ApplePaymentData;
	paymentMethod: ApplePaymentMethod;
	transactionIdentifier: string;
};

const usePaymentOptions = ({
	onPaymentSuccess,
	priceBreakup,
}: {
	onPaymentSuccess: () => void;
	priceBreakup: GetPriceBreakUpResponse;
}) => {
	const [isProcessing, setIsProcessing] = useState(false);
	const setJourneyPaymentError = useQuoteStore(selectSetJourneyPaymentError);
	const encryptedJourneyId = useQuoteStore(selectEncryptedJourneyId);
	const paymentError = useQuoteStore(selectPaymentError);

	const { paymentPublicKey } = usePaymentPublicKey();
	const { getCurrencyAndVat } = useCurrencyAndVat();
	const paymentIdRef = useRef('');
	const isCardPaymentEnabled = true;
	const { apiConnectionOptions, onGeneralErrorHandling } = useAppPlatformContextOptions();
	const [payOption, setPayOption] = useState<PaymentRadioOptions | undefined>(undefined);
	const [isCardFormValid, setIsCardFormValid] = useState(false);

	const resetPaymentState = () => {
		setJourneyPaymentError(undefined);
	};

	useEffect(() => {
		resetPaymentState();
	}, []);

	const telrPaymentAuthorize = async (applePaymentDataInfo?: ApplePaymentDataInfo) => {
		const currencyAndVat = getCurrencyAndVat();

		let authorizePaymentData: TelrPaymentAuthorizeRequest = {
			encryptedJourneyId: encryptedJourneyId || '',
			paymentVendorProfileId: PaymentVendorProvider.TelrUAE,
			paymentProviderId: PaymentProvider.CreditOrDebit,
			payment: {
				discountAmount: priceBreakup?.discountAmount,
				amountWithoutVat: priceBreakup.premiumAmount,
				icpFees: priceBreakup?.icpFees,
				vatAmount: priceBreakup.vatAmount,
				currency: currencyAndVat?.currencyName,
				currencyType: currencyAndVat?.currencyId,
				dohFees: priceBreakup.dohFines,
				basmahFees: priceBreakup.basamaFees,
			},
		};

		if (payOption === PaymentRadioOptions.ApplePay && applePaymentDataInfo) {
			authorizePaymentData = {
				...authorizePaymentData,
				paymentProviderId: PaymentProvider.AppleId,
				payment: {
					...authorizePaymentData.payment,
					providerExtension: {
						applePayTokenRequest: applePaymentDataInfo?.paymentData ?? ({} as ApplePaymentData),
						transactionIdentifier: applePaymentDataInfo.transactionIdentifier,
						paymentMethod: applePaymentDataInfo.paymentMethod,
					},
				},
			};
		}

		try {
			const { url3Ds, paymentId } = await telrAuthorizePayment(apiConnectionOptions)(authorizePaymentData);

			paymentIdRef.current = paymentId;

			if (payOption === PaymentRadioOptions.ApplePay && applePaymentDataInfo) return paymentId;

			return url3Ds;
		} catch (error) {
			const paymentAuthorizeError = error as PaymentErrors;

			if (paymentAuthorizeError?.mapped?.[0]?.code) {
				setJourneyPaymentError({
					errorCode: paymentAuthorizeError.mapped[0].code,
					errorMessage: paymentAuthorizeError?.mapped?.[0]?.message ?? '',
				});
			} else {
				await onGeneralErrorHandling(error as GlobalAPIError);
			}
		}
	};

	const authorizePayment = async (cardToken?: string, applePaymentData?: ApplePaymentData) => {
		setIsProcessing(true);
		const currencyAndVat = getCurrencyAndVat();

		let authorizePaymentData: PaymentAuthorizeRequest = {
			encryptedJourneyId: encryptedJourneyId || '',
			paymentProviderId: PaymentProvider.CreditOrDebit,
			paymentVendorProfileId: PaymentVendorProvider.CheckoutUAE,
			payment: {
				discountAmount: priceBreakup?.discountAmount,
				amountWithoutVat: priceBreakup.premiumAmount,
				icpFees: priceBreakup?.icpFees,
				vatAmount: priceBreakup.vatAmount,
				currency: currencyAndVat?.currencyName,
				currencyType: currencyAndVat?.currencyId,
				dohFees: priceBreakup.dohFines,
				basmahFees: priceBreakup.basamaFees,
			},
		};

		if (payOption === PaymentRadioOptions.CreditOrDebit) {
			authorizePaymentData = {
				...authorizePaymentData,
				cardToken: cardToken,
				paymentProviderId: PaymentProvider.CreditOrDebit,
				payment: {
					...authorizePaymentData.payment,
				},
			};
		} else if (payOption === PaymentRadioOptions.ApplePay) {
			authorizePaymentData = {
				...authorizePaymentData,
				paymentProviderId: PaymentProvider.AppleId,
				payment: {
					...authorizePaymentData.payment,
					providerExtension: {
						applePayTokenRequest: applePaymentData ?? ({} as ApplePaymentData),
					},
				},
			};
		}

		try {
			const { paymentId, url3Ds } = await paymentAuthorize(apiConnectionOptions)(authorizePaymentData);

			paymentIdRef.current = paymentId;

			if (payOption === PaymentRadioOptions.CreditOrDebit) return url3Ds;

			return paymentId;
		} catch (error) {
			const paymentAuthorizeError = error as PaymentErrors;

			if (paymentAuthorizeError?.mapped?.[0]?.code) {
				setJourneyPaymentError({
					errorCode: paymentAuthorizeError.mapped[0].code,
					errorMessage: paymentAuthorizeError?.mapped?.[0]?.message ?? '',
				});
			} else {
				await onGeneralErrorHandling(error as GlobalAPIError);
			}

			setIsProcessing(false);
		}
	};

	async function capturePayment() {
		setIsProcessing(true);

		const capturePaymentRequest = {
			encryptedJourneyId: encryptedJourneyId || '',
			paymentId: paymentIdRef.current,
		};

		try {
			await paymentCapture(apiConnectionOptions)(capturePaymentRequest);

			onPaymentSuccess();
		} catch (error) {
			const paymentCaptureError = error as PaymentErrors;

			if (paymentCaptureError?.mapped?.[0]?.code) {
				setJourneyPaymentError({
					errorCode: paymentCaptureError.mapped[0].code,
					errorMessage: paymentCaptureError?.mapped?.[0]?.message ?? '',
				});
			} else {
				await onGeneralErrorHandling(error as GlobalAPIError);
			}

			setIsProcessing(false);
		}
	}

	return {
		paymentPublicKey,
		authorizePayment,
		capturePayment,
		paymentError,
		isProcessingPayment: isProcessing,
		resetPaymentState,
		isCardPaymentEnabled,
		payOption,
		setPayOption,
		isCardFormValid,
		setIsCardFormValid,
		telrPaymentAuthorize,
	};
};

export default usePaymentOptions;
